import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import BackIconButton from '../components/BackIconButton';
import { computeDocumentState, DOCUMENT_STATES, formatDateBritish, docLabelFor, todayIso } from '../lib/compliance';
import { loadDocumentStream, versionFilesFromStream, logComplianceEvent } from '../lib/complianceEvents';
import { pickLiveVersion } from '../lib/documentDrafts';
import {
  approveDocument, requestInfoOnDocument, rejectDocument, replyOnDocument, nudgeSupplier,
  REQUEST_INFO_REASONS, REJECT_REASONS, MIN_NOTES_LENGTH, reasonLabel,
} from '../lib/complianceDecisions';
import { nudgeEligibility, nudgeExplanation, nudgeInputsFromStream } from '../lib/complianceNudge';
import { scrollBehavior } from '../lib/motion';
import { useActionToast } from '../components/ActionToast';

// Per-document detail page (document-detail-page brief §1) — one chronological
// stream of a supplier's document, newest-first, merging the version/decision
// spine (compliance_document_events) with the conversation
// (compliance_clarification_messages). Every version's file stays downloadable,
// including rejected ones. Header states the facts incl. "Blocking work".
//
// Decisions (approve / request info / reject / reply) run HERE, under the
// history (§6): you decide on the page where you just read the evidence. The
// review page still exists for the QUEUE flow — auto-advance to the next
// document, the expiry-confirm step, the eco badge — and BOTH surfaces call
// lib/complianceDecisions, so the two can never drift apart. Rob's call
// 2026-08-12: keep both, share the logic.
//
// The supplier sees the same stream, privacy-filtered, at
// /portal/compliance/:documentKey/history (SupplierDocumentHistory).

const STATE_LABEL = {
  [DOCUMENT_STATES.OUTSTANDING]:         { text: 'Outstanding', dot: '#E0290A' },
  [DOCUMENT_STATES.AWAITING_REVIEW]:     { text: 'Awaiting review', dot: '#2E8FA1' },
  [DOCUMENT_STATES.NEEDS_CLARIFICATION]: { text: 'Needs clarification', dot: '#E89A2E' },
  [DOCUMENT_STATES.EXPIRING_SOON]:       { text: 'Expiring soon', dot: '#E89A2E' },
  [DOCUMENT_STATES.EXPIRED]:             { text: 'Expired', dot: '#E0290A' },
  [DOCUMENT_STATES.APPROVED]:            { text: 'Approved', dot: '#92D400' },
  [DOCUMENT_STATES.NOT_REQUIRED]:        { text: 'Not required', dot: '#51534A' },
};

const METHOD_LABEL = {
  upload: 'Upload', download_return: 'Download & return', form: 'Hosted form', acknowledge: 'Acknowledge',
};

// One stream entry's headline + tone by type. Messages render as bubbles; the
// rest as spine markers.
const EVENT_META = {
  uploaded:                { label: 'Uploaded',               tone: '#2E8FA1' },
  approved:                { label: 'Approved',               tone: '#3B6D11' },
  rejected:                { label: 'Not accepted',           tone: '#B71F06' },
  clarification_requested: { label: 'More information requested', tone: '#7A4E10' },
  expiry_approved:         { label: 'Expiry approved',        tone: '#3B6D11' },
  acknowledged:            { label: 'Acknowledged',           tone: '#3B6D11' },
  superseded:              { label: 'Superseded',             tone: '#51534A' },
  required:                { label: 'Required from supplier', tone: '#51534A' },
  archived:                { label: 'Archived',               tone: '#51534A' },
  unarchived:              { label: 'Restored from archive',  tone: '#3B6D11' },
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

export default function ComplianceDocumentDetail() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();

  const [doc, setDoc] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [meta, setMeta] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [stream, setStream] = useState([]);
  const [staffNames, setStaffNames] = useState({}); // internal_users.id → full_name
  // §2: the stream reads DOWNWARDS by default. loadDocumentStream returns
  // newest-first, which is right for a queue but wrong here: half this stream
  // is a conversation, and showing the supplier's upload above the question it
  // answers makes the story run backwards. Newest-first stays available for
  // reading it as a decision record.
  const [oldestFirst, setOldestFirst] = useState(true);
  // §6 — decisions and replies happen HERE, at the bottom of the stream you
  // just read, instead of sending you to another screen to act on what you've
  // seen. Same logic as the review page (lib/complianceDecisions).
  const [action, setAction] = useState('idle'); // 'idle' | 'request_info' | 'reject'
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [reply, setReply] = useState('');
  const [approveExpiry, setApproveExpiry] = useState('');
  const [acting, setActing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { notify, Toast } = useActionToast();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error: docErr } = await supabase
      .from('supplier_onboarding_documents')
      .select('*, suppliers(id, supplier_name, category, status, risk_flag, compliance_status_override)')
      .eq('id', docId)
      .single();
    if (docErr || !data) { setError(docErr?.message || 'Document not found.'); setLoading(false); return; }
    setDoc(data);
    setSupplier(data.suppliers || null);
    const [{ data: fd }, { data: reqs }, streamRows] = await Promise.all([
      // A document key now spans several rows — the live version, superseded
      // ones, and possibly an unpublished draft. Take the live version (never a
      // draft); .maybeSingle() would just error on the second version.
      supabase.from('fusion_documents')
        .select('compliance_doc_key, document_name, satisfied_by, supplier_provides_expiry, status, version_value')
        .eq('compliance_doc_key', data.document_key),
      supabase.from('risk_document_requirements')
        .select('risk_level').eq('document_key', data.document_key),
      loadDocumentStream(data.supplier_id, data.document_key),
    ]);
    setMeta(pickLiveVersion(fd));
    setTiers([...new Set((reqs || []).map(r => r.risk_level))].sort());
    setStream(streamRows);

    // Resolve staff authors so the stream can name the person (§7).
    const staffIds = [...new Set([
      ...streamRows.filter(s => s.side === 'staff' && s.actorUserId).map(s => s.actorUserId),
      data.archived_by,
    ].filter(Boolean))];
    if (staffIds.length > 0) {
      const { data: users } = await supabase.from('internal_users').select('id, full_name').in('id', staffIds);
      setStaffNames(Object.fromEntries((users || []).map(u => [u.id, u.full_name])));
    }
    setLoading(false);
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  // --- Action hub. Mirrors SupplierDetail's upsertOnboardingDoc: patch the
  // row, clear a manual override if one is pinned (a doc change means auto is
  // the truth again), recompute the supplier status, reload. Confirmations via
  // the shared toast. Reset/delete navigate to the supplier since the row goes.
  const runPatch = useCallback(async (patch, successMsg, logType) => {
    if (!doc || saving) return;
    setSaving(true);
    const { error: e } = await supabase.from('supplier_onboarding_documents')
      .upsert({ supplier_id: doc.supplier_id, document_key: doc.document_key, updated_at: new Date().toISOString(), ...patch }, { onConflict: 'supplier_id,document_key' });
    if (e) { notify({ title: 'Could not update', body: e.message, tone: 'danger' }); setSaving(false); return; }
    if (doc.suppliers?.compliance_status_override) {
      await supabase.from('suppliers').update({ compliance_status_override: null }).eq('id', doc.supplier_id);
    }
    await supabase.rpc('recompute_supplier_compliance_status', { p_supplier_id: doc.supplier_id });
    if (logType) logComplianceEvent({ supplierId: doc.supplier_id, documentKey: doc.document_key, eventType: logType, actorSide: 'staff', actorUserId: currentInternalUser?.id || null, meta: patch.expiry_date || doc.expiry_date ? { expiry_date: patch.expiry_date || doc.expiry_date } : {} });
    await load();
    setSaving(false);
    if (successMsg) notify({ title: successMsg, tone: 'success' });
  }, [doc, saving, notify, load, currentInternalUser?.id]);

  const resetDoc = useCallback(async () => {
    if (!doc || saving) return;
    if (!window.confirm('Reset this document back to "Not Published"? This clears any recorded publish/signed dates and the expiry.')) return;
    setSaving(true);
    const { error: e } = await supabase.from('supplier_onboarding_documents').delete().eq('supplier_id', doc.supplier_id).eq('document_key', doc.document_key);
    if (e) { notify({ title: 'Could not reset', body: e.message, tone: 'danger' }); setSaving(false); return; }
    await supabase.rpc('recompute_supplier_compliance_status', { p_supplier_id: doc.supplier_id });
    notify({ title: 'Document reset to not published', tone: 'success' });
    navigate(`/suppliers/${doc.supplier_id}`);
  }, [doc, saving, notify, navigate]);

  // §5: Archive replaces Delete. A supplier's upload is evidence — it proves
  // what they gave us and when, and after the versioning work a signature can
  // point at it. Deleting one leaves an approval with nothing behind it. So
  // nothing is destroyed: the row leaves the working views, the file and the
  // whole history stay, and it can be brought back.
  const setArchived = useCallback(async (archive) => {
    if (!doc || saving) return;
    if (archive && !window.confirm(
      'Archive this document? It disappears from the supplier\'s compliance list and your review queue, and the requirement returns to outstanding. The file and its full history are kept, and you can unarchive it.'
    )) return;
    setSaving(true);
    const { error: e } = await supabase
      .from('supplier_onboarding_documents')
      .update({
        archived_at: archive ? new Date().toISOString() : null,
        archived_by: archive ? (currentInternalUser?.id || null) : null,
      })
      .eq('id', doc.id);
    if (e) {
      notify({ title: archive ? 'Could not archive' : 'Could not unarchive', body: e.message, tone: 'danger' });
      setSaving(false);
      return;
    }
    logComplianceEvent({
      supplierId: doc.supplier_id,
      documentKey: doc.document_key,
      eventType: archive ? 'archived' : 'unarchived',
      actorSide: 'staff',
      actorUserId: currentInternalUser?.id || null,
      meta: { fileName: doc.supplier_uploaded_file_name || null },
    });
    supabase.from('compliance_audit_log').insert([{
      actor_id: currentInternalUser?.id || null,
      action: 'override',
      supplier_id: doc.supplier_id,
      document_key: doc.document_key,
      meta: { type: archive ? 'archived' : 'unarchived', fileName: doc.supplier_uploaded_file_name || null },
    }]).then(() => {});
    await supabase.rpc('recompute_supplier_compliance_status', { p_supplier_id: doc.supplier_id });
    notify({ title: archive ? 'Document archived' : 'Document restored', tone: archive ? 'archive' : 'success' });
    setSaving(false);
    load();
  }, [doc, saving, notify, currentInternalUser?.id, load]);

  // One handler for all four — every one of them reloads the page afterwards so
  // the stream shows what just happened, which is the point of deciding here.
  const runDecision = useCallback(async (kind) => {
    if (!doc || acting) return;
    setActing(kind);
    const shared = {
      doc,
      supplier,
      documentLabel: meta?.document_name || doc.document_key,
      actorId: currentInternalUser?.id || null,
      source: 'detail_page',
    };
    let res;
    if (kind === 'approve') {
      res = await approveDocument({
        ...shared,
        // Only pass an expiry when the reviewer actually confirmed one —
        // undefined leaves the stored date alone.
        confirmedExpiry: doc.expiry_date ? (approveExpiry || String(doc.expiry_date).slice(0, 10)) : undefined,
      });
    } else if (kind === 'request_info') {
      res = await requestInfoOnDocument({ ...shared, reason, reasonLabel: reasonLabel(reason, REQUEST_INFO_REASONS), notes });
    } else if (kind === 'reject') {
      res = await rejectDocument({ ...shared, reason, reasonLabel: reasonLabel(reason, REJECT_REASONS), notes });
    } else {
      res = await replyOnDocument({ ...shared, body: reply });
    }
    setActing(null);
    if (res?.error) {
      notify({ title: 'Could not save', body: res.error, tone: 'danger' });
      return;
    }
    const titles = { approve: 'Approved', request_info: 'Information requested', reject: 'Rejected', reply: 'Reply sent' };
    notify({
      title: titles[kind],
      // A decision nobody was told about is half a decision — say so. `warning`
      // is the optional approval comment failing to save: the approval stands,
      // but the supplier won't see what you wrote, and that shouldn't pass as
      // an unqualified success.
      body: res?.warning || (res?.noAddress ? 'No email sent — this supplier has no active portal user.' : undefined),
      tone: (res?.warning || res?.noAddress) ? 'archive' : 'success',
    });
    setAction('idle'); setReason(''); setNotes(''); setReply(''); setApproveExpiry('');
    load();
  }, [doc, supplier, meta, acting, reason, notes, reply, approveExpiry, currentInternalUser?.id, notify, load]);

  if (currentInternalUser && !isAdmin(currentInternalUser)) {
    return (
      <div className="min-h-screen bg-canvas flex">
        <Sidebar currentInternalUser={currentInternalUser} />
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <p className="font-['Metropolis'] text-lg text-ink">This page is restricted to Admin and Super User accounts.</p>
        </div>
      </div>
    );
  }

  const state = doc ? computeDocumentState(doc) : null;
  const stateInfo = (state && STATE_LABEL[state]) || { text: '—', dot: '#51534A' };
  const label = docLabelFor(meta?.document_name, doc?.document_key);
  const method = meta?.satisfied_by || 'upload';
  const versionFiles = versionFilesFromStream(stream);
  const currentVersion = versionFiles[0]?.version || (doc?.supplier_uploaded_at ? 1 : null);
  const approved = !!doc?.expiry_approved_at;
  // Required docs block the AWARD until approved (never the invite).
  const blocking = !approved;
  // Lifecycle flags gating the action hub (mirrors the SupplierDetail row menu).
  const rd = doc?.review_decision;
  const withSupplier = rd === 'rejected' || rd === 'needs_clarification';
  const notRequired = !!doc?.not_required;
  const published = !!doc?.sent_at;
  const signed = !!doc?.signed_at;
  const hasUpload = !!doc?.supplier_uploaded_file_url;
  const pendingApproval = !notRequired && !withSupplier && !!doc?.expiry_date && !doc?.expiry_approved_at;
  const archived = !!doc?.archived_at;
  const canArchive = hasUpload && isAdmin(currentInternalUser);
  // Days until the supplier's proposed expiry — drives the §8 warning shown
  // before approval. Negative = already past.
  const expiryDaysLeft = doc?.expiry_date
    ? Math.round((new Date(`${String(doc.expiry_date).slice(0, 10)}T00:00:00Z`) - new Date(`${todayIso()}T00:00:00Z`)) / 86400000)
    : null;
  // §9: "Mark signed" is for documents Fusion sends out and receives back
  // signed on paper. Where the method captures the signature itself (hosted
  // form, acknowledgement, PDF signing) it means nothing and only invites a
  // wrong click, so it's hidden rather than mislabelled.
  const signatureIsCaptured = ['form', 'acknowledge', 'pdf_sign'].includes(method);
  // One chronological order for the whole stream — events and messages
  // together, never grouped by type. loadDocumentStream sorts newest-first;
  // reverse it for the default reading order.
  const orderedStream = oldestFirst ? [...stream].reverse() : stream;

  // §3 — Nudge, not Chase. Chase is for a supplier who never sent anything;
  // here they did, we asked a question, and they've gone quiet. Everything the
  // rules need comes from the stream: when we last asked, whether they've been
  // back since, and when we last nudged.
  // Derivation is shared with the review page, which also offers Nudge — see
  // nudgeInputsFromStream. Two screens computing "when did we last nudge?"
  // separately is how a cooldown quietly stops working.
  const nudgeInputs = nudgeInputsFromStream(stream, { withSupplier, fallbackAskedAt: doc?.reviewed_at || null });
  const nudge = nudgeEligibility(nudgeInputs);
  const lastAsk = nudgeInputs.lastAsk;
  const nudgeNote = nudgeExplanation(nudge);

  const runNudge = async () => {
    if (!nudge.canNudge || acting) return;
    setActing('nudge');
    const res = await nudgeSupplier({
      doc,
      supplier,
      documentLabel: meta?.document_name || doc.document_key,
      question: lastAsk?.body || doc.review_notes || null,
      askedAt: lastAsk?.at || null,
      actorId: currentInternalUser?.id || null,
    });
    setActing(null);
    notify({
      title: res?.emailed ? 'Nudge sent' : 'Nudge not sent',
      body: res?.noAddress ? 'This supplier has no active portal user, so there was nobody to email.' : undefined,
      tone: res?.emailed ? 'success' : 'danger',
    });
    if (res?.emailed) load();
  };

  const Fact = ({ k, children }) => (
    <div className="min-w-0">
      <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.14em] text-[#51534A] mb-1">{k}</div>
      <div className="font-['Metropolis'] text-[13px] text-ink">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-canvas flex">
      <Sidebar currentInternalUser={currentInternalUser} />
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-8 pt-8 pb-16 max-w-[1000px]">
          <div className="mb-4"><BackIconButton to="/compliance" label="Back to queue" /></div>

          {loading ? (
            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">Loading…</p>
          ) : error ? (
            <p className="font-['Metropolis'] text-sm text-[#E0290A]">{error}</p>
          ) : (
            <>
              {/* Archived — say it before anything else on the page, because
                  every fact below describes a document nobody else can see. */}
              {archived && (
                <div className="mb-5 rounded-2xl border-[1.5px] border-[#9BA08C]/50 bg-[#9BA08C]/[0.10] px-5 py-4">
                  <div className="font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#51534A] mb-1">Archived</div>
                  <p className="font-['Metropolis'] text-[13px] text-ink leading-snug">
                    Archived {formatDateBritish(String(doc.archived_at).slice(0, 10))}
                    {staffNames[doc.archived_by] ? ` by ${staffNames[doc.archived_by]}` : ''}. It's hidden from
                    the supplier's compliance list and your review queue — the file and everything below are kept.
                  </p>
                </div>
              )}

              {/* Header — facts card */}
              <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift overflow-hidden mb-5">
                <div aria-hidden="true" className="h-1 w-full" style={{ background: stateInfo.dot }} />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <h1 className="font-['Metropolis'] text-[24px] font-extrabold text-ink leading-tight">{label}</h1>
                      <div className="font-['Metropolis'] text-[13px] text-fusion-mid-grey mt-1">
                        {supplier ? (
                          <button type="button" onClick={() => navigate(`/suppliers/${supplier.id}`)}
                            className="text-[#2E8FA1] hover:text-active-green font-semibold underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer">
                            {supplier.supplier_name}
                          </button>
                        ) : '—'}
                        {' · '}{METHOD_LABEL[method] || method}
                        {tiers.length > 0 && <> · Required at {tiers.map(t => `Tier ${t}`).join(', ')}</>}
                      </div>
                    </div>
                    <button type="button" onClick={() => navigate(`/compliance/${docId}/review`)}
                      className="flex-shrink-0 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-white bg-[#2F6E14] border border-[#2F6E14] rounded-full px-5 py-2.5 hover:brightness-110 transition-[filter]">
                      Review in the queue
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-line">
                    <Fact k="Status">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: stateInfo.dot }} aria-hidden="true" />
                        {stateInfo.text}
                      </span>
                    </Fact>
                    <Fact k="Current version">{currentVersion ? `v${currentVersion}` : '—'}</Fact>
                    <Fact k="Expiry">
                      {doc?.expiry_date
                        ? <>{formatDateBritish(doc.expiry_date)}{!approved && <span className="text-[#7A4E10]"> · awaiting approval</span>}</>
                        : 'No expiry'}
                    </Fact>
                    <Fact k="Blocking work">
                      {blocking
                        ? <span className="text-[#B71F06] font-semibold">Yes, until approved</span>
                        : <span className="text-[#3B6D11] font-semibold">No — approved</span>}
                    </Fact>
                  </div>
                </div>
              </div>

              {/* Action hub — the lifecycle actions that used to live in the
                  SupplierDetail row's ••• menu, now on the detail page
                  (document-detail-page §2). The review-loop decisions
                  (approve/reject/request info) live under the history below —
                  these are lifecycle actions, which is a different job. */}
              <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-5 mb-5">
                <div className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-fusion-forest mb-3">Manage</div>
                {notRequired ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-['Metropolis'] text-[13px] text-[#51534A]">Marked not required for this supplier.</span>
                    <button type="button" disabled={saving}
                      onClick={() => runPatch({ not_required: false, not_required_at: null, not_required_by: null }, 'Marked as required')}
                      className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-2 border border-line text-fusion-mid-grey hover:border-[#DADCCD] disabled:opacity-50">
                      Mark as required
                    </button>
                  </div>
                ) : (
                  <>
                    {/* §4: the expiry is READ-ONLY here. It used to be a live
                        date field whose onChange silently approved the
                        document — a side-effect nobody asked for. Confirming or
                        correcting an expiry is part of the APPROVE action,
                        beside the file, so it lives with the decisions below. */}
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <span className="font-['Metropolis'] text-[12px] font-semibold text-[#51534A]">Expiry date</span>
                      <span className="font-['Metropolis'] text-[13px] text-ink">
                        {doc?.expiry_date ? formatDateBritish(doc.expiry_date) : 'None recorded'}
                      </span>
                      {approved && (
                        <>
                          <span className="font-['Metropolis'] text-[12px] text-[#3B6D11]">Approved{doc?.expiry_approved_at ? ` ${formatDateBritish(String(doc.expiry_approved_at).slice(0, 10))}` : ''}</span>
                          <button type="button" disabled={saving}
                            onClick={() => runPatch({ expiry_approved_at: null, expiry_approved_by: null }, 'Approval removed')}
                            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-2 border border-line text-fusion-mid-grey hover:border-[#DADCCD] disabled:opacity-50">
                            Un-approve
                          </button>
                        </>
                      )}
                    </div>

                    {/* §8: a supplier-entered expiry that's nearly gone. Say it
                        BEFORE the approval, not after — approving this puts them
                        straight into expiring-soon. */}
                    {pendingApproval && expiryDaysLeft != null && expiryDaysLeft <= 30 && (
                      <p className="font-['Metropolis'] text-[12px] font-semibold text-[#7A4E10] bg-[#E89A2E]/[0.10] border border-[#E89A2E]/35 rounded-lg px-3 py-2 mb-3 leading-snug">
                        {expiryDaysLeft < 0
                          ? `This expiry has already passed (${formatDateBritish(doc.expiry_date)}). Approving it would record an expired document.`
                          : `Expires in ${expiryDaysLeft} day${expiryDaysLeft === 1 ? '' : 's'} (${formatDateBritish(doc.expiry_date)}). Approving puts them straight into expiring-soon.`}
                      </p>
                    )}

                    {/* Points DOWN the page, not off it. The decisions moved to
                        the bottom of the stream (§6) but this line still sent
                        people to the review page, which is the one thing the
                        change was meant to stop. */}
                    <p className="font-['Metropolis'] text-[11px] text-[#9BA08C] mb-4">
                      These are lifecycle actions. To approve, reject, request info or reply,{' '}
                      <button
                        type="button"
                        onClick={() => {
                          const el = document.getElementById('detail-reply');
                          el?.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
                          el?.focus();
                        }}
                        className="underline underline-offset-2 text-[#2E8FA1] hover:text-fusion-forest rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#2E8FA1]">
                        use the decisions under the history
                      </button>.
                    </p>

                    {/* Lifecycle */}
                    <div className="flex flex-wrap gap-2">
                      {!published && (
                        <button type="button" disabled={saving} onClick={() => runPatch({ sent_at: new Date().toISOString(), sent_by: currentInternalUser?.id || null }, 'Published to portal')}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-2 border border-line text-fusion-mid-grey hover:border-[#DADCCD] disabled:opacity-50">Publish to portal</button>
                      )}
                      {published && !signed && !signatureIsCaptured && (
                        <button type="button" disabled={saving}
                          title="Record that the supplier returned this document signed on paper"
                          onClick={() => runPatch({ signed_at: new Date().toISOString(), signed_marked_by: currentInternalUser?.id || null }, 'Marked signed')}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-2 border border-line text-fusion-mid-grey hover:border-[#DADCCD] disabled:opacity-50">Mark signed on paper</button>
                      )}
                      {!published && !signed && (
                        <button type="button" disabled={saving} onClick={() => runPatch({ not_required: true, not_required_at: new Date().toISOString(), not_required_by: currentInternalUser?.id || null }, 'Marked not required')}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-2 border border-line text-fusion-mid-grey hover:border-[#DADCCD] disabled:opacity-50">Mark not required</button>
                      )}
                      {(published || signed) && (
                        <button type="button" disabled={saving} onClick={resetDoc}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-2 border border-[#E0290A]/40 text-[#E0290A] hover:bg-[#E0290A]/[0.06] disabled:opacity-50">Reset to not published</button>
                      )}
                      {canArchive && !archived && (
                        <button type="button" disabled={saving} onClick={() => setArchived(true)}
                          title="Hides it from the supplier's list and your queue. Nothing is deleted."
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-2 border border-line text-fusion-mid-grey hover:border-[#DADCCD] disabled:opacity-50">Archive document</button>
                      )}
                      {archived && (
                        <button type="button" disabled={saving} onClick={() => setArchived(false)}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-2 border border-[#92D400]/50 text-fusion-forest hover:bg-[#92D400]/[0.08] disabled:opacity-50">Unarchive</button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Every version's file — retained, newest-first */}
              {versionFiles.length > 0 && (
                <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-5 mb-5">
                  <div className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-fusion-forest mb-3">Files by version</div>
                  <ul className="space-y-1.5">
                    {versionFiles.map(f => (
                      <li key={f.id} className="flex items-center justify-between gap-3 text-[13px]">
                        <span className="font-['Metropolis'] text-ink">
                          <span className="font-bold">v{f.version || '?'}</span>
                          <span className="text-fusion-mid-grey"> · {f.fileName || 'Document'} · {fmtDateTime(f.at)}</span>
                        </span>
                        <a href={f.fileUrl} target="_blank" rel="noopener noreferrer"
                          className="flex-shrink-0 font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.15em] text-fusion-forest hover:text-ink">
                          Download
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* The chronological stream — one ordered list, newest-first */}
              <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-5">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-fusion-forest">History</div>
                  {stream.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setOldestFirst(v => !v)}
                      className="font-['Metropolis'] text-[11px] font-semibold text-[#2E8FA1] hover:text-active-green bg-transparent border-0 p-0 cursor-pointer">
                      {oldestFirst ? 'Oldest first ↓' : 'Newest first ↑'}
                    </button>
                  )}
                </div>
                {stream.length === 0 ? (
                  <p className="font-['Metropolis'] text-[13px] text-fusion-mid-grey">Nothing recorded for this document yet.</p>
                ) : (
                  <ol className="space-y-3">
                    {orderedStream.map(item => {
                      // §7: name the individual, not just the org. A supplier
                      // reading "FUSION 2K asked you a question" has nobody to
                      // reply to. Staff entries resolve created_by →
                      // "Danielle M · Fusion 2K"; rows predating that column
                      // (and the migration's backfill) fall back to the org.
                      const staffName = item.actorUserId ? staffNames[item.actorUserId] : null;
                      const sideLabel = item.side === 'supplier'
                        ? (supplier?.supplier_name || 'Supplier')
                        : item.side === 'system'
                          ? 'System'
                          : staffName ? `${staffName} · Fusion 2K` : 'Fusion 2K';
                      if (item.kind === 'message') {
                        const isSupplier = item.side === 'supplier';
                        return (
                          <li key={item.id} className={`flex flex-col ${isSupplier ? 'items-end' : 'items-start'}`}>
                            <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.14em] mb-1" style={{ color: isSupplier ? '#2E8FA1' : '#7A4E10' }}>
                              {sideLabel}{item.at ? ` · ${fmtDateTime(item.at)}` : ''}
                            </div>
                            <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 font-['Metropolis'] text-[13px] text-ink leading-snug whitespace-pre-wrap ${isSupplier ? 'bg-[#2E8FA1]/[0.08]' : 'bg-[#E89A2E]/[0.08]'}`}>
                              {item.body}
                              {item.fileUrl && (
                                <a href={item.fileUrl} target="_blank" rel="noopener noreferrer" className="block mt-1 font-semibold underline underline-offset-2 text-[#2E8FA1]">
                                  {item.fileName || 'View attachment'}
                                </a>
                              )}
                            </div>
                          </li>
                        );
                      }
                      const em = EVENT_META[item.type] || { label: item.type, tone: '#51534A' };
                      const expiry = item.meta?.expiry_date ? ` · expires ${formatDateBritish(item.meta.expiry_date)}` : '';
                      // Auto-approval must never read as a person's decision
                      // (hosted-forms §5). Name the rule, and say when a
                      // submission was HELD for review rather than approved.
                      const autoApproved = item.type === 'approved' && item.meta?.auto === true;
                      const autoHeld = item.type === 'required' && item.meta?.auto_approval;
                      const autoHeldReason = autoHeld === 'sampled'
                        ? `Held for review — spot check (${item.meta?.sample_review_pct ?? '?'}% of submissions)`
                        : item.meta?.reason === 'identity_edited'
                          ? 'Held for review — the supplier changed their prefilled details'
                          : item.meta?.reason === 'expiry_entered'
                            ? 'Held for review — an expiry date needs confirming'
                            : 'Held for review';
                      return (
                        <li key={item.id} className="flex items-start gap-3">
                          <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ background: em.tone }} aria-hidden="true" />
                          <div className="min-w-0">
                            <div className="font-['Metropolis'] text-[13px] text-ink">
                              <span className="font-bold" style={{ color: em.tone }}>
                                {autoApproved ? 'Auto-approved by rule' : autoHeld ? autoHeldReason : em.label}
                              </span>
                              {item.type === 'uploaded' && item.version ? <span className="text-fusion-mid-grey"> v{item.version}</span> : null}
                              <span className="text-fusion-mid-grey">
                                {' '}· {autoApproved || autoHeld ? 'No reviewer' : sideLabel} · {fmtDateTime(item.at)}{expiry}
                              </span>
                            </div>
                            {item.body && <div className="font-['Metropolis'] text-[12px] text-[#51534A] italic mt-0.5 whitespace-pre-wrap">{item.body}</div>}
                            {item.type === 'uploaded' && item.fileUrl && (
                              <a href={item.fileUrl} target="_blank" rel="noopener noreferrer" className="font-['Metropolis'] text-[11px] font-semibold underline underline-offset-2 text-[#2E8FA1]">
                                {item.fileName || 'View file'}
                              </a>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}

                {/* §6 — decide from the page you just read it on. Archived
                    documents are out of the working flow, so no actions. */}
                {!archived && (
                  <div className="mt-5 pt-5 border-t border-[#E6EADB]">
                    <label htmlFor="detail-reply" className="block font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.14em] text-[#51534A] mb-1.5">
                      Reply to the supplier
                    </label>
                    <textarea
                      id="detail-reply"
                      rows={2}
                      value={reply}
                      onChange={e => setReply(e.target.value)}
                      placeholder="Answer their question. This doesn't change the document's status."
                      className="w-full bg-white border border-line rounded-lg px-3 py-2 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#9BA08C] focus:outline-none focus:border-[#92D400] resize-y" />
                    <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
                      <p className="font-['Metropolis'] text-[11px] text-[#9BA08C]">They get an email and see it on their compliance page.</p>
                      <button type="button" disabled={!reply.trim() || !!acting}
                        onClick={() => runDecision('reply')}
                        className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-2 border border-[#2E8FA1]/40 text-[#2E8FA1] hover:border-[#2E8FA1] disabled:opacity-40">
                        {acting === 'reply' ? 'Sending…' : 'Send reply'}
                      </button>
                    </div>

                    {/* §3 Nudge — only while a question is genuinely open, and
                        it says why when it can't go yet rather than sitting
                        there greyed out with no explanation. */}
                    {(nudge.canNudge || nudgeNote) && (
                      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap rounded-xl border border-[#E89A2E]/35 bg-[#E89A2E]/[0.07] px-3.5 py-2.5">
                        <p className="font-['Metropolis'] text-[12px] text-[#7A4E10] leading-snug">
                          {nudge.canNudge
                            ? `No reply for ${nudge.workingDaysWaited} working days.${nudge.daysSinceNudge != null ? ` Last nudged ${nudge.daysSinceNudge} days ago.` : ''}`
                            : nudgeNote}
                        </p>
                        {nudge.canNudge && (
                          <button type="button" disabled={!!acting} onClick={runNudge}
                            className="flex-shrink-0 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-2 text-white bg-[#E89A2E] border border-[#E89A2E] hover:brightness-110 disabled:opacity-50">
                            {acting === 'nudge' ? 'Sending…' : 'Nudge'}
                          </button>
                        )}
                      </div>
                    )}

                    {action === 'idle' ? (
                      <div className="flex flex-wrap gap-2 mt-4">
                        <button type="button" disabled={!!acting} onClick={() => runDecision('approve')}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-2 text-white bg-[#2F6E14] border border-[#2F6E14] hover:brightness-110 disabled:opacity-50">
                          {acting === 'approve' ? 'Approving…' : 'Approve'}
                        </button>
                        <button type="button" disabled={!!acting} onClick={() => { setReason(''); setNotes(''); setAction('request_info'); }}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-2 border border-[#E89A2E]/50 text-[#7A4E10] hover:bg-[#E89A2E]/[0.08] disabled:opacity-50">
                          Request info
                        </button>
                        <button type="button" disabled={!!acting} onClick={() => { setReason(''); setNotes(''); setAction('reject'); }}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-2 border border-[#E0290A]/40 text-[#E0290A] hover:bg-[#E0290A]/[0.06] disabled:opacity-50">
                          Reject
                        </button>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-xl border border-line bg-[#F7F9F0] p-4">
                        <div className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.14em] text-[#51534A] mb-2">
                          {action === 'reject' ? 'Reject this document' : 'Ask for more information'}
                        </div>
                        <select value={reason} onChange={e => setReason(e.target.value)}
                          className="w-full bg-white border border-line rounded-lg px-3 py-2 font-['Metropolis'] text-[13px] text-ink mb-2 focus:outline-none focus:border-[#92D400]">
                          <option value="">Choose a reason…</option>
                          {(action === 'reject' ? REJECT_REASONS : REQUEST_INFO_REASONS).map(r => (
                            <option key={r.key} value={r.label}>{r.label}</option>
                          ))}
                        </select>
                        <textarea
                          rows={3}
                          value={notes}
                          onChange={e => setNotes(e.target.value)}
                          placeholder="Tell them exactly what's wrong and what to do about it — they read this word for word."
                          className="w-full bg-white border border-line rounded-lg px-3 py-2 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#9BA08C] focus:outline-none focus:border-[#92D400] resize-y" />
                        <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
                          <p className="font-['Metropolis'] text-[11px] text-[#9BA08C]">
                            {notes.trim().length < MIN_NOTES_LENGTH ? 'A reason on its own isn\'t enough — say what they need to do.' : 'They get this verbatim by email.'}
                          </p>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => { setAction('idle'); setReason(''); setNotes(''); }}
                              className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-2 border border-line text-[#51534A] hover:border-[#DADCCD]">
                              Cancel
                            </button>
                            <button type="button"
                              disabled={!reason || notes.trim().length < MIN_NOTES_LENGTH || !!acting}
                              onClick={() => runDecision(action)}
                              className={`font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-2 text-white disabled:opacity-40 ${action === 'reject' ? 'bg-[#E0290A] border border-[#E0290A]' : 'bg-[#E89A2E] border border-[#E89A2E]'} hover:brightness-110`}>
                              {acting ? 'Sending…' : action === 'reject' ? 'Reject' : 'Send request'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <Toast />
    </div>
  );
}
